import { Component, inject, OnInit } from '@angular/core';
import {
  AuthService,
  Benefit,
  MemberDetails,
  MemberQuery,
  MemberService,
  RegistrationStepQuery,
  RegistrationStepService,
  RegistrationStepSettings,
  StepType,
  toNumber,
} from '@fgb/core';
import { iif, lastValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';

type BenefitOption = Benefit & { selected: boolean };

@Component({
  selector: 'fgbcl-registration-benefit-options',
  templateUrl: './registration-benefits-options.component.html',
  styleUrl: './registration-benefits-options.component.scss',
})
export class RegistrationBenefitOptionsComponent implements OnInit {
  readonly stepType = StepType.BenefitOptions;
  private authService = inject(AuthService);
  private memberService = inject(MemberService);
  private registrationStepQuery = inject(RegistrationStepQuery);
  private registrationStepService = inject(RegistrationStepService);
  private memberQuery = inject(MemberQuery);
  memberDetails$: Observable<MemberDetails | undefined>;
  benefits: BenefitOption[] = [];
  isLoaded: boolean = false;
  numOfSelections: number = -1;

  ngOnInit(): void {
    const user = this.registrationStepQuery.getStep(StepType.PersonalDetails)?.Data;
    this.memberDetails$ = iif(
      () => this.authService.isAuthenticated() && !user.IsFirstTimeUser,
      this.memberService.fetchMemberDetails().pipe(switchMap(() => this.memberQuery.selectMemberDetails())),
      of(this.registrationStepQuery.getStep(StepType.PersonalDetails)?.Data)
    );
    const settings = this.registrationStepQuery.getStepSettings(this.stepType) as RegistrationStepSettings;
    // Default to 1 selection if not set or set to 0 (unlimited)
    if (settings?.NumberOfSelections !== '0') {
      this.numOfSelections = toNumber(settings?.NumberOfSelections) || 1;
    }

    lastValueFrom(
      this.registrationStepService.fetchRegistrationStepForCurrentWizard(this.stepType, true).pipe(
        map((step) => step.Data),
        tap(({ Benefits, Selection }) => {
          this.benefits =
            (Benefits.map((benefit: Benefit) => ({
              ...benefit,
              selected: Selection.Benefits?.includes(benefit.BenefitId) || false,
            })) as BenefitOption[]) || [];

          if (this.benefits?.length > 0) {
            this.isLoaded = true;
          }
        })
      )
    );
  }

  toggleSelect(id: number): void {
    let benefit: any = this.benefits.find((b: BenefitOption) => b.BenefitId === id);
    const value = !benefit.selected;
    benefit.selected = value;
  }

  submitStep(): void {
    if (
      this.selectedBenefits.length > 0 &&
      (this.isUnlimitedSelections || this.selectedBenefits.length === this.numOfSelections)
    ) {
      lastValueFrom(this.registrationStepService.submitRegistrationStepData(this.stepType, { Benefits: this.selectedBenefits }));
    }
  }

  get selectedBenefits(): number[] {
    return this.benefits?.filter((b) => b.selected).map((b: BenefitOption) => b.BenefitId);
  }

  get isUnlimitedSelections(): boolean {
    return this.numOfSelections === -1;
  }
}
